import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { LoginModal } from '@/components/auth/LoginModal'

interface AuthOverlayProps {
  /** Content to render (shown normally when authenticated, greyed out when not) */
  children: React.ReactNode
  /** Whether the user is authenticated */
  isAuthenticated: boolean
  /** Additional CSS classes */
  className?: string
  /** Callback when login is successful */
  onLoginSuccess?: (data: { isNewAccount: boolean }) => void
}

/**
 * AuthOverlay - Always renders children, but greys them out with login overlay when not authenticated
 */
export const AuthOverlay: React.FC<AuthOverlayProps> = ({
  children,
  isAuthenticated,
  className,
  onLoginSuccess
}) => {
  const { t } = useTranslation(['app', 'common'])
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)

  // If authenticated, just show the content normally
  if (isAuthenticated) {
    return <div className={className}>{children}</div>
  }

  // Not authenticated - show greyed out content with login overlay
  return (
    <>
      <div className={cn("relative", className)}>
        {/* Greyed out children content */}
        <div className="opacity-40 pointer-events-none select-none">
          {children}
        </div>

        {/* Login overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-background/95 border border-border rounded-xl p-6 shadow-2xl flex flex-col items-center gap-4 max-w-xs mx-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-6 h-6 text-primary" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-foreground mb-1">
                {t('auth.loginRequired', 'Login Required')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('auth.loginToView', 'Sign in to view your content')}
              </p>
            </div>
            <Button
              onClick={() => setIsLoginModalOpen(true)}
              className="w-full"
            >
              {t('auth.login', 'Log in')}
            </Button>
          </div>
        </div>
      </div>

      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={onLoginSuccess}
      />
    </>
  )
}

export default AuthOverlay
